/** @format */

import { useRef, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import billingValidationSchema from 'validation/Registration/BillingSchema';
import form from '../authForm.module.css';
import AuthField from '../AuthField/AuthField';
import PostalCodeFields from '../PostalCodeFields/PostalCodeFields';
import { errorToast, successToast } from 'components/common/Toast/Toast';

const BillingForm = ({
  billingData,
  setBillingData,
  contactData,
  handleBack,
  setSubmitted,
  newsAccept,
  subscribeNewsletter
}) => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const [initialValues] = useState(() => ({
    companyName: billingData?.companyName || '',
    billingVatNumber: billingData?.billingVatNumber || '',
    billingAccountNumber: billingData?.billingAccountNumber || '',
    billingCountry: billingData?.billingCountry || contactData.contactCountry,
    billingCity: billingData?.billingCity || contactData.contactCity,
    billingZipCode: billingData?.billingZipCode || contactData.contactZipCode,
    billingStreetAndNumber: billingData?.billingStreetAndNumber || contactData.contactStreetAndNumber
  }));

  // Start from the contact address; PostalCodeFields reads zip code and city from billingData.
  useEffect(() => {
    if (!billingData?.billingCity && !billingData?.billingZipCode) {
      setBillingData({
        ...billingData,
        billingCity: contactData?.contactCity || '',
        billingZipCode: contactData?.contactZipCode || ''
      });
    }
  }, []);

  useEffect(() => {
    formikRef.current.setFieldValue('billingCity', billingData?.billingCity);
    formikRef.current.setFieldValue('billingZipCode', billingData?.billingZipCode);
  }, [billingData?.billingCity, billingData?.billingZipCode]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={billingValidationSchema(t)}
      onSubmit={async (values, { setSubmitting }) => {
        if (newsAccept) {
          let value = contactData;
          await subscribeNewsletter({ value }).then((res) => {
            if (res) {
              successToast(t('billingForm.newsletter.success'));
              setSubmitting(true);
              setBillingData(values);
              setSubmitted(true);
              setSubmitting(false);
            } else {
              errorToast(t('billingForm.newsletter.error'));
            }
          });
        } else {
          setSubmitting(true);
          setBillingData(values);
          setSubmitted(true);
          setSubmitting(false);
        }
      }}>
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
        const bind = (name) => ({
          id: name,
          name,
          value: values[name],
          onChange: (e) => {
            handleChange(e);
            setBillingData({ ...billingData, [name]: e.target.value });
          },
          onBlur: handleBlur,
          error: touched[name] && errors[name]
        });

        return (
          <form className={form.form} onSubmit={handleSubmit} noValidate>
            <div className={form.row}>
              <AuthField
                {...bind('companyName')}
                label={t('billingForm.companyName')}
                autoComplete='organization'
                autoFocus
              />
              <AuthField {...bind('billingVatNumber')} label={t('billingForm.vatNumber')} placeholder='12345678-1-12' />
            </div>
            <AuthField {...bind('billingAccountNumber')} label={t('billingForm.accountNumber')} />
            <h2 className={form.sectionTitle}>{t('billingForm.billingAddress')}</h2>
            <div className={`${form.row} ${form.rowZipCity}`}>
              <PostalCodeFields
                data={billingData}
                setData={setBillingData}
                billing={true}
                zipHelper={touched.billingZipCode}
                zipError={errors.billingZipCode}
                cityHelper={touched.billingCity}
                cityError={errors.billingCity}
              />
            </div>
            <div className={`${form.row} ${form.rowStreetCountry}`}>
              <AuthField
                {...bind('billingStreetAndNumber')}
                label={t('billingForm.streetAndNumber')}
                autoComplete='address-line1'
              />
              <AuthField {...bind('billingCountry')} label={t('billingForm.country')} disabled />
            </div>
            <div className={form.actions}>
              <button
                type='button'
                className={`${form.button} ${form.buttonGhost}`}
                onClick={() => {
                  setBillingData({ ...values });
                  handleBack();
                }}>
                {t('billingForm.buttons.back')}
              </button>
              <button type='submit' className={`${form.button} ${form.buttonPrimary}`}>
                {t('billingForm.buttons.register')}
              </button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};

export default BillingForm;
